import React, { useRef, useCallback, useEffect } from 'react';
import Editor, { Monaco } from '@monaco-editor/react';
import type { editor } from 'monaco-editor';
import { registerReferenceProviders } from './providers/referenceProviders';
import { modelManager } from '@/utils/MonacoModelManager';

interface ValidationError {
  line: number;
  column?: number;
  message: string;
  severity?: string;
  source?: string;
}

interface MonacoEditorProps {
  value: string;
  onChange: (value: string) => void;
  contentType?: string;
  validationErrors?: ValidationError[];
  onOpenReference?: (itemId: string) => Promise<void>;
  readOnly?: boolean;
}

const EDITOR_OPTIONS: editor.IStandaloneEditorConstructionOptions = {
  minimap: { enabled: true },
  lineNumbers: 'on',
  wordWrap: 'on',
  fontSize: 14,
  fontFamily: '"Fira Code", "Monaco", "Menlo", monospace',
  scrollBeyondLastLine: false,
  automaticLayout: true,
  tabSize: 2,
  insertSpaces: true,
  renderWhitespace: 'selection',
  bracketPairColorization: { enabled: true },
  guides: {
    bracketPairs: true,
    indentation: true,
  },
  quickSuggestions: {
    other: true,
    comments: false,
    strings: true,
  },
};

const CUSTOM_THEME: editor.IStandaloneThemeData = {
  base: 'vs',
  inherit: true,
  rules: [
    { token: 'frontmatter', foreground: '6A9955' },
    { token: 'frontmatter.key', foreground: '0070C1' },
    { token: 'frontmatter.value', foreground: 'A31515' },
    { token: 'template.variable', foreground: 'E040FB', fontStyle: 'bold' },
    { token: 'custom.tag', foreground: '795548' },
  ],
  colors: {
    'editor.background': '#FFFFFF',
    'editor.lineHighlightBackground': '#F5F5F5',
    'editorLineNumber.foreground': '#999999',
    'editorGutter.background': '#FAFAFA',
  },
};

export function MonacoEditor({
  value,
  onChange,
  contentType,
  validationErrors = [],
  onOpenReference,
  readOnly = false,
}: MonacoEditorProps) {
  const editorRef = useRef<editor.IStandaloneCodeEditor | null>(null);
  const monacoRef = useRef<Monaco | null>(null);
  const decorationsRef = useRef<string[]>([]);
  const providersRef = useRef<{ dispose: () => void } | null>(null);

  // Keep a ref to onOpenReference to avoid re-registering providers
  const openRefRef = useRef(onOpenReference);
  openRefRef.current = onOpenReference;

  const handleEditorDidMount = useCallback((editor: editor.IStandaloneCodeEditor, monaco: Monaco) => {
    editorRef.current = editor;
    monacoRef.current = monaco;

    // Initialize model manager
    modelManager.initialize(monaco);

    // Define custom theme
    monaco.editor.defineTheme('content-editor-theme', CUSTOM_THEME);
    monaco.editor.setTheme('content-editor-theme');

    // Register custom language for markdown with frontmatter
    if (!monaco.languages.getLanguages().some((lang) => lang.id === 'markdown-frontmatter')) {
      monaco.languages.register({ id: 'markdown-frontmatter' });

      monaco.languages.setMonarchTokensProvider('markdown-frontmatter', {
        tokenizer: {
          root: [
            [/^---\s*$/, { token: 'frontmatter.delimiter', next: '@frontmatter' }],
            [/\{\{\s*[\w.]+\s*\}\}/, 'template.variable'],
            [/![a-zA-Z]+\s+[^\n]+/, 'custom.tag'],
            [/./, { token: '', next: '@markdown' }],
          ],
          frontmatter: [
            [/^---\s*$/, { token: 'frontmatter.delimiter', next: '@markdown' }],
            [/^\s*[\w_-]+(?=\s*:)/, 'frontmatter.key'],
            [/:/, 'frontmatter.delimiter'],
            [/"[^"]*"|'[^']*'/, 'frontmatter.value'],
            [/\d+/, 'frontmatter.value'],
            [/true|false/, 'frontmatter.value'],
            [/![a-zA-Z]+/, 'custom.tag'],
            [/#.*$/, 'comment'],
            [/./, 'frontmatter'],
          ],
          markdown: [
            [/^#{1,6}\s.*$/, 'keyword'],
            [/\*\*[^*]+\*\*/, 'strong'],
            [/\*[^*]+\*/, 'emphasis'],
            [/```[\s\S]*?```/, 'string'],
            [/`[^`]+`/, 'string'],
            [/\{\{\s*[\w.]+\s*\}\}/, 'template.variable'],
            [/\[[^\]]+\]\([^)]+\)/, 'string.link'],
            [/^[\s]*[-*+]\s/, 'keyword'],
            [/^[\s]*\d+\.\s/, 'keyword'],
          ],
        },
      });

      // Register reference providers (only once when language is registered)
      providersRef.current = registerReferenceProviders(
        monaco,
        async (itemId) => {
          if (openRefRef.current) {
            await openRefRef.current(itemId);
          }
        },
        'markdown-frontmatter'
      );
    }

    // Add keyboard shortcuts
    editor.addAction({
      id: 'save-draft',
      label: 'Save Draft',
      keybindings: [monaco.KeyMod.CtrlCmd | monaco.KeyCode.KeyS],
      run: () => {
        document.dispatchEvent(new CustomEvent('editor-save'));
      },
    });

    editor.addAction({
      id: 'publish',
      label: 'Publish',
      keybindings: [monaco.KeyMod.CtrlCmd | monaco.KeyMod.Shift | monaco.KeyCode.KeyS],
      run: () => {
        document.dispatchEvent(new CustomEvent('editor-publish'));
      },
    });
  }, []);

  // Update decorations for validation errors
  useEffect(() => {
    if (!editorRef.current || !monacoRef.current) return;

    const monaco = monacoRef.current;
    const editor = editorRef.current;

    const newDecorations = validationErrors.map((error) => {
      const severity = error.severity || 'error';
      const color = severity === 'warning' ? '#ed6c02' : '#d32f2f';

      return {
        range: new monaco.Range(error.line, 1, error.line, 1000),
        options: {
          isWholeLine: true,
          className: severity === 'warning' ? 'validation-warning-line' : 'validation-error-line',
          glyphMarginClassName:
            severity === 'warning' ? 'validation-warning-glyph' : 'validation-error-glyph',
          hoverMessage: {
            value: `**${severity === 'warning' ? 'Warning' : 'Error'}**: ${error.message}${error.source === 'pyodide' ? ' _(Schema)_' : ''}`,
          },
          overviewRuler: {
            color,
            position: monaco.editor.OverviewRulerLane.Right,
          },
        },
      };
    });

    decorationsRef.current = editor.deltaDecorations(decorationsRef.current, newDecorations);
  }, [validationErrors]);

  const handleChange = useCallback(
    (newValue: string | undefined) => {
      if (newValue !== undefined) {
        onChange(newValue);
      }
    },
    [onChange]
  );

  return (
    <div className="h-full w-full">
      <style>
        {`
          .validation-error-line {
            background-color: rgba(211, 47, 47, 0.1) !important;
          }
          .validation-error-glyph {
            background: #d32f2f;
            border-radius: 50%;
            width: 8px !important;
            height: 8px !important;
            margin-left: 4px;
            margin-top: 6px;
          }
          .validation-warning-line {
            background-color: rgba(237, 108, 2, 0.1) !important;
          }
          .validation-warning-glyph {
            background: #ed6c02;
            border-radius: 50%;
            width: 8px !important;
            height: 8px !important;
            margin-left: 4px;
            margin-top: 6px;
          }
        `}
      </style>
      <Editor
        height="100%"
        language="markdown-frontmatter"
        value={value}
        onChange={handleChange}
        onMount={handleEditorDidMount}
        options={{
          ...EDITOR_OPTIONS,
          readOnly,
        }}
        loading={
          <div className="flex items-center justify-center h-full text-muted-foreground">
            Loading editor...
          </div>
        }
      />
    </div>
  );
}

export default MonacoEditor;
